import React, { useContext } from "react";
import { Popover2 } from "@blueprintjs/popover2";
import Link from "next/link";
import { StoresContext } from "../../../pages/_app";
import { Button, Icon, Tag, PopoverInteractionKind, Checkbox } from "@blueprintjs/core";
import { humanBaseSize } from "../../../utils/size";
import { renderTagTooltip } from "../../../utils/metadata-formatter";
import { CollectionName, GenomeInList } from "../../../api";
import { observer } from "mobx-react";
import { constructGenomeDisplayName } from "../../../utils/genomes";
import FavoriteStar from "../../favorite-star";

// Keep this synced with styles/atcc.scss::$search-result-colors
const NUM_SEARCH_RESULT_COLORS = 7;

interface GenomeRowProps {
  collectionName: CollectionName | null;
  genome: GenomeInList;
  filterByTag: (tag: string) => void;
  toggleGenomeStarring: (starred: boolean, uuid: string) => void;
  rowNum: number;
}

const GenomeRow = (props: GenomeRowProps) => {
  const rootStore = useContext(StoresContext);
  // Popup is only used for ATCC
  const { created_at, uuid, primary_assembly } = props.genome;
  const isLoggedIn = rootStore.userStore.isLoggedIn();
  const { isItemSelected } = rootStore.genomeStore;

  const renderGenomeSimilarity = () => {
    const percentage = ((props.genome.primary_assembly?.ani || 0.0) * 100).toFixed(2);
    return <i>{percentage === "100.00" ? "> 99.99" : percentage}%</i>;
  };

  const renderSearchSimilarity = () => {
    const numMatchingBases = props.genome.primary_assembly?.num_matching_bases || 0;
    const percentMatchingBases = props.genome.primary_assembly?.percent_matching_bases || 0;
    return (
      <>
        {numMatchingBases} base{numMatchingBases === 1 ? "" : "s"} matched (
        <i>{percentMatchingBases}%</i>)
      </>
    );
  };

  const renderTags = () => {
    return props.genome.tags.map((t) => {
      return (
        <Popover2
          key={t.name}
          interactionKind={PopoverInteractionKind.HOVER}
          placement="top"
          content={renderTagTooltip(t.name)}
          popoverClassName="info-tooltip"
        >
          <Tag interactive={true} onClick={() => props.filterByTag(t.name)} className="genome-tag">
            {t.name}
          </Tag>
        </Popover2>
      );
    });
  };

  // ATCC will have a download link, default will have the taxon name since the two columns
  // are swapped between the portals
  const renderGenomeName = () => {
    if (!props.genome.external_url) {
      return props.genome.name;
    }
    const atccUrl = props.genome.external_url;
    return (
      <a target="_blank" rel="noopener noreferrer" href={atccUrl}>
        {props.genome.name}
        <Icon icon="share" className="atcc-link--icon" />
      </a>
    );
  };

  const color = ((props.rowNum - 1) % NUM_SEARCH_RESULT_COLORS) + 1;

  const onMouseEnter = () => {
    rootStore.genomeStore.setHoveredGenome({ uuid: props.genome.uuid, color });
  };

  const onMouseLeave = () => {
    rootStore.genomeStore.setHoveredGenome(null);
  };

  const className = rootStore.genomeStore.sequenceSearchEnabled ? `result-${color}` : "";

  return (
    <tr onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <td className={className}>
        <Checkbox
          checked={isItemSelected(props.genome.uuid)}
          onChange={() => rootStore.genomeStore.toggleSelected(props.genome.uuid)}
        />
      </td>
      <td className="genome-star">
        <FavoriteStar
          userLoggedIn={isLoggedIn}
          genome={props.genome}
          toggleStarring={props.toggleGenomeStarring}
        />
      </td>
      <td className="taxon-name">
        <Popover2
          interactionKind={PopoverInteractionKind.HOVER}
          placement={"top"}
          content={"Click to go to overview page"}
          popoverClassName="info-tooltip"
        >
          <Link as={`/genomes/${uuid}`} href={`/genomes/[uuid]`}>
            <Button minimal onClick={() => rootStore.genomeStore.resetSearchFilters()}>
              {constructGenomeDisplayName(props.genome, props.collectionName)}
            </Button>
          </Link>
        </Popover2>
      </td>
      <td className="genome-name">{renderGenomeName()}</td>
      {rootStore.genomeStore.relatedGenomeSearchEnabled && <td>{renderGenomeSimilarity()}</td>}
      {rootStore.genomeStore.sequenceSearchEnabled && <td>{renderSearchSimilarity()}</td>}
      <td className="strain-name">{props.genome.attributes.atcc_metadata?.strain_name ?? ""}</td>
      <td className="genome-tags">{renderTags()}</td>
      <td>
        {new Date(created_at).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })}
      </td>
      <td>
        {primary_assembly && primary_assembly.derived_metadata.length
          ? humanBaseSize(primary_assembly.derived_metadata.length)
          : 0}
      </td>
    </tr>
  );
};

export default observer(GenomeRow);
